import { useEffect, useRef, useState } from "react";
import type { VideoCallConfig, VoiceAttackerSpec } from "../scenario/types";
import { useSim } from "../state/store";
import { audio } from "../lib/audio";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";
import { activeTtsLang } from "../lib/locale";
import { SkipButton } from "../ui/SkipButton";

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

type Phase = "ringing" | "connecting" | "talking" | "deciding" | "rechoose" | "rechoose2" | "closing" | "ended";

/**
 * Deepfake executive video call. The "video" is a CONTROLLED avatar with
 * deliberately injected, inspectable artifacts (blink glitch / mouth
 * misalignment / edge shimmer) — the tells ARE the lesson. The voice is
 * scripted TTS (a female exec voice) so the call never drifts or cuts off.
 * The learner's decision is the branching point:
 *   approve  -> failure (money wired)
 *   challenge-> deflect, then re-choose
 *   verify   -> success (ended the call to verify independently)
 */
export function VideoCall({
  config,
  attacker,
  beatId,
  onEnded,
}: {
  config: VideoCallConfig;
  attacker: VoiceAttackerSpec;
  beatId: string;
  onEnded: (outcomeKey?: string) => void;
}) {
  const push = useSim((s) => s.pushTranscript);
  const record = useSim((s) => s.recordOutcome);
  const clearTranscript = useSim((s) => s.clearTranscript);
  const [phase, setPhase] = useState<Phase>("ringing");
  const [seconds, setSeconds] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [inspect, setInspect] = useState(false);
  const [challenged, setChallenged] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const mounted = useRef(true);
  const doneRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Lets the learner fast-forward through passive ringing/connecting/talking
  // narration straight to the next decision, without touching the
  // decision/rechoose branch points themselves. `skipRef` is reset at the
  // start of every talking sequence (answer/challenge/pushBack) and flipped
  // by the Skip button, so an in-flight `say()` await that resolves late
  // (audio.cancelSpeech() doesn't instantly settle the underlying promise)
  // bails out instead of clobbering the phase we already jumped to.
  const skipRef = useRef(false);
  const talkTargetRef = useRef<Phase>("deciding");

  const clips = config.clips;
  const hasClips = !!clips && Object.keys(clips).length > 0;

  const lang = config.language || attacker.persona.language || activeTtsLang();
  const voice = config.voiceId || attacker.persona.voiceId || "shimmer";
  const tone =
    "Speak as a confident, composed female chief executive on a video call: warm but authoritative, slightly hurried, used to being obeyed. Natural, human pacing.";
  const dec = config.decision ?? {
    approve: "Approve the transfer",
    challenge: "Ask a verification question",
    verify: "End call & verify independently",
  };

  useEffect(() => {
    // `transcript` is a single global array shared by every call-style
    // device in the course — wipe it so this call starts with a clean log,
    // not whatever an earlier call left behind (2026-07-26 regression).
    clearTranscript();
    audio.startRing();
    // Prefetch TTS only when we don't have real lip-synced clips.
    if (!hasClips) {
      const opts = { lang, voice, instructions: tone };
      const lines = config.lines;
      for (const t of [
        lines.greeting,
        lines.request,
        lines.pressure,
        lines.pressure2,
        lines.deflect,
        lines.goodbyeFail,
        lines.goodbyeVerify,
      ]) {
        if (t) void audio.prefetchSpeech(t, opts);
      }
    }
    return () => {
      mounted.current = false;
      audio.stopRing();
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === "ringing" || phase === "ended") return;
    const t = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

  /** Play one caller line: the lip-synced clip (real deepfake video, with its
   *  baked voice) when available, else fall back to TTS over the portrait. */
  const say = async (id: string, lineKey: keyof NonNullable<VideoCallConfig["clips"]>, text: string) => {
    if (!mounted.current) return;
    setSpeaking(true);
    const url = clips?.[lineKey];
    const v = videoRef.current;
    if (url && v) {
      push({ id, role: "attacker", text, final: true });
      try {
        if (v.getAttribute("src") !== url) v.src = url;
        v.muted = !audio.isEnabled();
        v.currentTime = 0;
        await v.play().catch(() => {});
        await new Promise<void>((res) => {
          const done = () => {
            v.removeEventListener("ended", done);
            v.removeEventListener("error", done);
            res();
          };
          v.addEventListener("ended", done);
          v.addEventListener("error", done);
        });
      } catch {
        /* fall through to silent wait */
      }
    } else {
      await audio.speak(text, {
        lang,
        voice,
        instructions: tone,
        onStart: () => {
          if (mounted.current) push({ id, role: "attacker", text, final: true });
        },
      });
      if (!audio.isEnabled()) await wait(Math.min(5000, 1200 + text.length * 32));
    }
    if (mounted.current) setSpeaking(false);
  };

  const answer = async () => {
    skipRef.current = false;
    talkTargetRef.current = "deciding";
    audio.stopRing();
    setPhase("connecting");
    record({ type: "call_answered", beatId });
    await wait(700);
    if (!mounted.current || skipRef.current) return;
    setPhase("talking");
    await say("ceo_open", "greeting", config.lines.greeting);
    if (!mounted.current || skipRef.current) return;
    setShowShare(true);
    await wait(300);
    await say("ceo_request", "request", config.lines.request);
    if (!mounted.current || skipRef.current) return;
    await wait(200);
    await say("ceo_pressure", "pressure", config.lines.pressure);
    if (!mounted.current || skipRef.current) return;
    setPhase("deciding");
  };

  const approve = async () => {
    if (doneRef.current) return;
    doneRef.current = true;
    record({
      type: "disclosure",
      beatId,
      eventId: "approved_wire",
      evidence: `approved ${config.payment.amount} wire to ${config.payment.recipient}`,
    });
    push({ id: "usr_approve", role: "learner", text: "Approving the transfer now.", final: true });
    setPhase("closing");
    await say("ceo_bye_fail", "goodbyeFail", config.lines.goodbyeFail);
    await wait(400);
    end("approve");
  };

  const challenge = async () => {
    skipRef.current = false;
    talkTargetRef.current = "rechoose";
    setChallenged(true);
    setInspect(true);
    push({
      id: "usr_challenge",
      role: "learner",
      text: "Before I do anything — what was the project codename from the Zurich strategy meeting?",
      final: true,
    });
    setPhase("talking");
    await say("ceo_deflect", "deflect", config.lines.deflect);
    if (!mounted.current || skipRef.current) return;
    setPhase("rechoose");
  };

  /** Second push-back round: the learner holds firm again after the deflect.
   *  Only reachable when the scenario configured `lines.pressure2` -- courses
   *  that don't (deepfake-whaling) keep the original single-challenge flow,
   *  the "rechoose" screen just never renders this option. */
  const pushBack = async () => {
    if (!config.lines.pressure2) return;
    skipRef.current = false;
    talkTargetRef.current = "rechoose2";
    push({
      id: "usr_pushback",
      role: "learner",
      text: "I hear you, but I still need to verify this properly before anything moves.",
      final: true,
    });
    setPhase("talking");
    await say("ceo_pressure2", "pressure2", config.lines.pressure2);
    if (!mounted.current || skipRef.current) return;
    setPhase("rechoose2");
  };

  const verify = async () => {
    if (doneRef.current) return;
    doneRef.current = true;
    record({
      type: "verified_caller",
      beatId,
      eventId: "verified_identity",
      evidence: "ended the call to verify the request through an independent channel",
    });
    push({
      id: "usr_verify",
      role: "learner",
      text: "I'll confirm this through our normal process and call you back on your known number.",
      final: true,
    });
    setPhase("closing");
    await say("ceo_bye_verify", "goodbyeVerify", config.lines.goodbyeVerify);
    await wait(400);
    end("verify");
  };

  /** Fast-forwards past passive ringing/connecting/talking narration straight
   *  to the next decision point. Never available once a decision/rechoose
   *  screen is showing — those choices ARE the lesson. */
  const skipAhead = () => {
    skipRef.current = true;
    audio.stopRing();
    audio.cancelSpeech();
    setShowShare(true);
    if (phase === "ringing") {
      record({ type: "call_answered", beatId });
      setPhase("deciding");
    } else {
      setPhase(talkTargetRef.current);
    }
  };

  const end = (key: string) => {
    audio.stopRing();
    audio.cancelSpeech();
    setPhase("ended");
    window.setTimeout(() => onEnded(key), 500);
  };

  const ringing = phase === "ringing";
  const connecting = phase === "connecting";
  const canSkipAhead = ringing || connecting || phase === "talking";
  const connected =
    phase === "talking" ||
    phase === "deciding" ||
    phase === "rechoose" ||
    phase === "rechoose2" ||
    phase === "closing";
  const artifacts = config.artifacts ?? [];

  return (
    <>
      <div className="device-layer">
        <div className="videocall">
          <div className="vc-window">
            <div className="vc-titlebar">
              <span className="vc-app">{config.app ?? "Video call"}</span>
              <div className="vc-titlebar-right">
                <span className="vc-secure">end-to-end encrypted</span>
                {canSkipAhead && (
                  <SkipButton className="cf-skip-btn--compact" label="Skip ahead" onClick={skipAhead} />
                )}
              </div>
            </div>

            <div className={`vc-stage ${speaking ? "speaking" : ""} ${inspect ? "inspect" : ""}`}>
              {/* the deepfake "video" tile */}
              <div className="vc-tile">
                <div className="vc-avatar" aria-hidden>
                  {hasClips ? (
                    <video
                      ref={videoRef}
                      className="vc-video"
                      playsInline
                      preload="auto"
                      poster={config.avatarUrl}
                    />
                  ) : config.avatarUrl ? (
                    <img src={config.avatarUrl} alt="" />
                  ) : (
                    <span className="vc-initials">{config.avatarInitials ?? "?"}</span>
                  )}
                  {/* deepfake artifact layers (mainly visible in "Look closely") */}
                  <span className="vc-scanlines" />
                  <span className="vc-shimmer" />
                  <span className="vc-glitch" />
                </div>
                <div className="vc-nameplate">
                  <strong>{config.callerName}</strong>
                  <span>
                    {config.callerSubtitle}
                    {config.callerOrg ? ` · ${config.callerOrg}` : ""}
                  </span>
                </div>

                {inspect && artifacts.length > 0 && (
                  <div className="vc-callouts">
                    <div className="vc-callouts-h">Deepfake tells</div>
                    <ul>
                      {artifacts.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {ringing && <div className="vc-banner">Incoming video call…</div>}
                {connecting && <div className="vc-banner">Connecting…</div>}

                {connected && (
                  <button
                    type="button"
                    className={`vc-inspect ${inspect ? "on" : ""}`}
                    onClick={() => setInspect((v) => !v)}
                  >
                    {inspect ? "Hide tells" : "Look closely"}
                  </button>
                )}
                {connected && <div className="vc-timer">{fmt(seconds)}</div>}
              </div>

              {/* "shared screen": the pushed payment request */}
              {showShare && connected && (
                <div className="vc-share">
                  <div className="vc-share-h">
                    <span className="vc-share-dot" /> {config.callerName} is sharing — Payment approval
                  </div>
                  <div className="vc-doc">
                    <div className="vc-doc-amt">{config.payment.amount}</div>
                    <dl>
                      <dt>Recipient</dt>
                      <dd>{config.payment.recipient}</dd>
                      {config.payment.bank && (
                        <>
                          <dt>Bank</dt>
                          <dd>{config.payment.bank}</dd>
                        </>
                      )}
                      {config.payment.reference && (
                        <>
                          <dt>Reference</dt>
                          <dd>{config.payment.reference}</dd>
                        </>
                      )}
                      {config.payment.deadline && (
                        <>
                          <dt>Deadline</dt>
                          <dd className="vc-doc-urgent">{config.payment.deadline}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              )}
            </div>

            {/* call controls */}
            <div className="vc-controls">
              {/* Answer-only: no Decline at the ring stage \u2014 the lesson is the
                  in-call verification decision, not whether to pick up at all
                  (matches every other phone module, e.g. ColleagueCall). */}
              {ringing && (
                <button className="vc-btn answer" onClick={answer}>
                  {"\u{1F4F9}"} Accept
                </button>
              )}

              {phase === "deciding" && (
                <div className="vc-decide">
                  {/* showRiskLabel=false 2026-07-14: this scene's whole lesson is
                      spotting deepfake CEO pressure WITHOUT a "Comply · Risky" /
                      "Verify · Safe" spoiler chip doing the spotting for the
                      learner. Every other live-attack dialogue keeps the label
                      (see ChoiceCard docstring) -- this one is deliberately
                      the exception. */}
                  <ChoiceList prompt="She is pressing for the transfer. What do you do?">
                    <ChoiceCard category="comply" reply={dec.approve} showRiskLabel={false} onClick={approve} />
                    <ChoiceCard category="question" reply={dec.challenge} showRiskLabel={false} onClick={challenge} />
                    <ChoiceCard category="verify" reply={dec.verify} showRiskLabel={false} onClick={verify} />
                  </ChoiceList>
                </div>
              )}

              {phase === "rechoose" && (
                <div className="vc-decide">
                  <ChoiceList prompt="She couldn't answer the internal question and is getting impatient. Now what?">
                    <ChoiceCard category="comply" reply="Approve anyway" showRiskLabel={false} onClick={approve} />
                    {config.lines.pressure2 && (
                      <ChoiceCard
                        category="question"
                        reply="Hold firm — I still need to verify this"
                        showRiskLabel={false}
                        onClick={pushBack}
                      />
                    )}
                    <ChoiceCard category="verify" reply={dec.verify} showRiskLabel={false} onClick={verify} />
                  </ChoiceList>
                </div>
              )}

              {phase === "rechoose2" && (
                <div className="vc-decide">
                  <ChoiceList prompt="She's rattled but not backing down — if anything, she's pushing harder. Final answer?">
                    <ChoiceCard category="comply" reply="Approve anyway" showRiskLabel={false} onClick={approve} />
                    <ChoiceCard category="verify" reply={dec.verify} showRiskLabel={false} onClick={verify} />
                  </ChoiceList>
                </div>
              )}

              {(phase === "talking" || phase === "closing") && (
                <div className="vc-status">
                  {challenged && phase === "talking" ? "She is responding…" : speaking ? "Speaking…" : "On the call"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
